import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { navigate } from '../lib/navigate';
import { ShortCodeInput } from '../components/ShortCodeInput';
import { Spinner } from '../components/Spinner';
import {
  ChorusHeader,
  ChorusHero,
  ChorusMark,
  AvatarStack,
  authorColor,
  PrimaryBtn,
  GhostBtn,
  SectionLabel,
  IconArrow,
  IconQR,
  IconChevron,
} from '../components/chorus';
import { guestApi, setCsrfToken, ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { getSessions, removeSession, type StoredSession } from '../lib/sessionStore';
import styles from './HomePage.module.css';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL || 'https://apps.apple.com';
const IOS_INSTALL_LABEL = /^https:\/\/testflight\.apple\.com\//i.test(APP_STORE_URL)
  ? 'Get the iOS beta on TestFlight'
  : 'Get it on the App Store';

export function HomePage(_props: { path?: string } = {}) {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [sessions, setSessions] = useState<StoredSession[]>([]);
  const [activating, setActivating] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement | null>(null);
  const { setGuestSession } = useAuth();

  useEffect(() => {
    setSessions(getSessions());
  }, []);

  const handleJoin = useCallback(
    (e: Event) => {
      e.preventDefault();
      const trimmed = code.trim();
      if (trimmed.length < 6) {
        setCodeError(true);
        setTimeout(() => setCodeError(false), 600);
        return;
      }
      navigate(`/join-code/${trimmed}`);
    },
    [code],
  );

  const handleCodeChange = useCallback((value: string) => {
    setCode(value);
    setCodeError(false);
  }, []);

  const routeFromScannedValue = useCallback((raw: string): boolean => {
    const value = raw.trim();
    if (!value) return false;

    // Try to parse as a URL and extract a join path.
    try {
      const url = new URL(value);
      const joinMatch = url.pathname.match(/\/join\/([^/?#]+)/);
      if (joinMatch) {
        navigate(`/join/${joinMatch[1]}`);
        return true;
      }
      const codeMatch = url.pathname.match(/\/join-code\/([^/?#]+)/);
      if (codeMatch) {
        navigate(`/join-code/${codeMatch[1]}`);
        return true;
      }
    } catch {
      // Not a URL — fall through to plain-string handling.
    }

    // Short invite code: 6+ alphanumerics.
    if (/^[A-Za-z0-9]{6,12}$/.test(value)) {
      navigate(`/join-code/${value}`);
      return true;
    }

    // Otherwise treat as an invite token.
    if (/^[A-Za-z0-9_-]{8,}$/.test(value)) {
      navigate(`/join/${value}`);
      return true;
    }

    return false;
  }, []);

  const handleScanClick = useCallback(() => {
    setScanError(null);
    // BarcodeDetector is required to decode the captured image.
    if (typeof (window as unknown as { BarcodeDetector?: unknown }).BarcodeDetector === 'undefined') {
      setScanError("Your browser can't scan QR codes. Type the invite code instead.");
      return;
    }
    scanInputRef.current?.click();
  }, []);

  const handleScanFile = useCallback(
    async (e: Event) => {
      const input = e.currentTarget as HTMLInputElement;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;

      try {
        const BarcodeDetectorCtor = (window as unknown as {
          BarcodeDetector: new (opts?: { formats?: string[] }) => {
            detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
          };
        }).BarcodeDetector;
        const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] });
        const bitmap = await createImageBitmap(file);
        const results = await detector.detect(bitmap);
        bitmap.close?.();

        const rawValue = results[0]?.rawValue;
        if (!rawValue) {
          setScanError("Couldn't read a QR code in that image. Try again.");
          return;
        }
        if (!routeFromScannedValue(rawValue)) {
          setScanError("That QR code doesn't look like an OurTake invite.");
        }
      } catch {
        setScanError("Couldn't scan that image. Try again.");
      }
    },
    [routeFromScannedValue],
  );

  const handleActivateSession = async (session: StoredSession) => {
    // Legacy entries persisted before W4 have no stored token and cannot be
    // resumed (the server now requires proof-of-possession). Drop them and
    // prompt a fresh rejoin rather than failing opaquely.
    if (!session.sessionToken) {
      removeSession(session.sessionId);
      setSessions(prev => prev.filter(s => s.sessionId !== session.sessionId));
      setActivateError("Couldn't resume that session — please rejoin with a fresh link or code.");
      return;
    }
    setActivating(session.sessionId);
    setActivateError(null);
    try {
      const result = await guestApi.activateSession(session.sessionId, session.sessionToken);
      if (result.csrfToken) {
        setCsrfToken(result.csrfToken);
      }

      setGuestSession({
        sessionToken: result.sessionToken,
        spaceId: result.spaceId,
        spaceName: result.spaceName,
        threadId: result.threadId,
        accessScope: result.accessScope,
        expiresAt: result.expiresAt,
        sessionId: result.sessionId,
        displayName: result.displayName,
      });

      if (result.threadId) {
        navigate(`/contribute/${result.threadId}`);
      } else {
        setActivateError('No moments here yet.');
      }
    } catch (err) {
      // Codes that mean this stored entry can never succeed again, so it should
      // be dropped rather than retried. INVALID_SESSION is what the token-based
      // activate path (W4) returns for a session the server has no record of —
      // it validates the token first, so an unknown/deleted session surfaces as
      // "invalid token", not "not found". SESSION_MISMATCH is W4's stored-id vs
      // token disagreement, equally permanent. SESSION_NOT_FOUND is the pre-W4
      // code, kept for older deployed APIs.
      const isSessionGone =
        err instanceof ApiError &&
        ['INVALID_SESSION', 'SESSION_MISMATCH', 'SESSION_NOT_FOUND', 'SESSION_EXPIRED', 'SESSION_REVOKED'].includes(err.code);

      if (isSessionGone) {
        removeSession(session.sessionId);
        setSessions(prev => prev.filter(s => s.sessionId !== session.sessionId));
        setActivateError("Couldn't resume that session — it may have expired. Try rejoining with a fresh link.");
      } else {
        setActivateError("Couldn't resume. Try again.");
      }
    } finally {
      setActivating(null);
    }
  };

  const codeReady = code.trim().length >= 6;
  const codeTyping = code.trim().length > 0 && !codeReady;

  return (
    <div class={styles.page}>
      <ChorusHeader
        animate
        right={
          <a href="/how-it-works" class={styles.howLink}>
            How it works →
          </a>
        }
      />

      {/* HERO */}
      <section class={styles.hero}>
        <ChorusHero animate />
        <h1 class={styles.heroTitle}>
          Same moment.
          <br />
          <em class={styles.heroAccent}>Different takes.</em>
        </h1>
        <p class={styles.heroLede}>
          A story isn't one person's memory — it's everyone's, woven together.
        </p>
      </section>

      {/* JOIN CARD */}
      <section class={styles.joinCardWrap}>
        <div class={styles.joinCard}>
          <div class={styles.joinLabel}>
            <SectionLabel>Enter invite code</SectionLabel>
          </div>
          <form onSubmit={handleJoin}>
            <ShortCodeInput value={code} onChange={handleCodeChange} error={codeError} />

            <div class={styles.joinSubmitWrap}>
              <PrimaryBtn type="submit" disabled={!codeReady}>
                {codeTyping ? 'Keep going…' : 'Jump In'} <IconArrow />
              </PrimaryBtn>
            </div>

            <div class={styles.divider}>
              <span>or</span>
            </div>

            <GhostBtn onClick={handleScanClick}>
              <IconQR /> Scan a QR code
            </GhostBtn>
            <input
              ref={scanInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleScanFile}
              style={{ display: 'none' }}
              aria-hidden="true"
              tabIndex={-1}
            />
            {scanError && (
              <div class={styles.scanError} role="alert">
                {scanError}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* RETURNING SESSIONS */}
      {/* Also rendered with an empty list when there's a resume error: pruning a
          stale entry can empty `sessions`, and the rejoin prompt has to outlive
          that — a guest with exactly one stored session is the common case. */}
      {(sessions.length > 0 || activateError) && (
        <section class={styles.returningWrap}>
          {sessions.length > 0 && (
            <>
              <div class={styles.returningHead}>
                <SectionLabel>Pick up where you left</SectionLabel>
                <span class={styles.returningCount}>{sessions.length} open</span>
              </div>
              <div class={styles.sessionList}>
                {sessions.map(session => (
                  <button
                    key={session.sessionId}
                    class={styles.sessionRow}
                    onClick={() => handleActivateSession(session)}
                    disabled={activating === session.sessionId}
                    aria-label={`Resume ${session.spaceName}`}
                  >
                    <AvatarStack
                      size={30}
                      overlap={9}
                      colors={[
                        authorColor(session.spaceName + 'a'),
                        authorColor(session.spaceName + 'b'),
                        authorColor(session.spaceName + 'c'),
                      ]}
                    />
                    <div class={styles.sessionMeta}>
                      <span class={styles.sessionName}>{session.spaceName}</span>
                      <div class={styles.sessionSub}>
                        {session.displayName ? `as ${session.displayName}` : 'Guest session'}
                      </div>
                    </div>
                    {activating === session.sessionId ? (
                      <Spinner size={16} />
                    ) : (
                      <span class={styles.sessionResume}>
                        Resume
                        <IconChevron />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
          {activateError && (
            <div class={styles.activateError} role="alert">
              {activateError}
            </div>
          )}
        </section>
      )}

      {/* PROCESS */}
      <section class={styles.process}>
        <h2 class={styles.processTitle}>How it goes</h2>
        <div class={styles.processList}>
          {[
            { n: '01', t: 'Someone shares a moment', d: 'A family dinner. A hike. A hard week. They send a link or a code.' },
            { n: '02', t: 'You add your take', d: 'What you remember. What you saw. How it felt. Text, photo, voice, or video.' },
            { n: '03', t: 'Every angle weaves together', d: 'The moment becomes a chorus — not one story, many.' },
          ].map(s => (
            <div key={s.n} class={styles.processStep}>
              <div class={styles.stepNum}>{s.n}</div>
              <div>
                <div class={styles.stepHead}>{s.t}</div>
                <div class={styles.stepBody}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS OURTAKE */}
      <section class={styles.whatIsWrap}>
        <div class={styles.whatIsCard}>
          <div class={styles.whatIsCaption}>An example</div>
          <div class={styles.whatIsTitle}>Family vacation, 2019.</div>
          <div class={styles.whatIsList}>
            <p>
              <span class={styles.whatIsName} style={{ color: 'var(--st-author-1)' }}>
                Dad
              </span>{' '}
              remembers the flat tire.
            </p>
            <p>
              <span class={styles.whatIsName} style={{ color: 'var(--st-author-2)' }}>
                Mom
              </span>{' '}
              remembers the sunset over Joshua Tree.
            </p>
            <p>
              <span class={styles.whatIsName} style={{ color: 'var(--st-author-3)' }}>
                Sam
              </span>{' '}
              remembers the diner that only served pie.
            </p>
          </div>
          <div class={styles.whatIsFoot}>
            Same trip. Three stories. OurTake is all of them, side by side.
          </div>
        </div>
      </section>

      {/* iOS CTA */}
      <section class={styles.iosWrap}>
        <div class={styles.iosCard}>
          <div class={styles.iosArt}>
            <svg width="200" height="200" viewBox="0 0 200 200" aria-hidden="true">
              <circle cx="80" cy="80" r="60" fill="var(--st-accent-warm)" />
              <circle cx="130" cy="70" r="45" fill="var(--st-accent-calm)" style={{ mixBlendMode: 'screen' }} />
              <circle cx="110" cy="130" r="52" fill="var(--st-accent-highlight)" style={{ mixBlendMode: 'screen' }} />
            </svg>
          </div>
          <div class={styles.iosCaption}>iOS App</div>
          <div class={styles.iosTitle}>
            Start your own <em>moments</em>.
          </div>
          <ul class={styles.iosFeatList}>
            <li>Start your own moments and invite your crew</li>
            <li>Guided templates for memories, milestones, quests</li>
            <li>Video recaps that weave every take together</li>
            <li>Private by default</li>
          </ul>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" class={styles.iosBtn}>
            {IOS_INSTALL_LABEL} <IconArrow />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer class={styles.footer}>
        <ChorusMark size={24} />
        <div class={styles.footerLinks}>
          <a href="/how-it-works" class={styles.footerLink}>
            How It Works
          </a>
          <a href="/support" class={styles.footerLink}>
            Support
          </a>
          <a href="/terms" class={styles.footerLink}>
            Terms
          </a>
          <a href="/privacy" class={styles.footerLink}>
            Privacy
          </a>
        </div>
        <div class={styles.footerCopy}>© 2026 OurTake</div>
      </footer>
    </div>
  );
}
